/**
 * Workspace-only multi-member picker — same directory UX as
 * ``SupervisorMemberPicker`` but operates on an *array* of selected
 * emails (chip list) instead of a single string.
 *
 * Used by release-flow co-supervisors and pool-level agent supervisor
 * overrides so assignment surfaces stay consistent with the Workbench.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getRestApiBearerToken } from '../../services/apiToken.js';
import { listWorkspaceMembers } from '../../services/workspaceApi.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function norm(s) {
  return (s || '').trim().toLowerCase();
}

export default function WorkspaceMembersMultiPicker({
  value,
  onChange,
  tenantId,
  excludeEmails,
  placeholder,
  emptyHint,
  disabled = false,
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const selectedEmails = useMemo(
    () => (Array.isArray(value) ? value.map(norm).filter(Boolean) : []),
    [value],
  );
  const selectedSet = useMemo(() => new Set(selectedEmails), [selectedEmails]);
  const excludedSet = useMemo(
    () => new Set((excludeEmails || []).map(norm).filter(Boolean)),
    [excludeEmails],
  );

  const load = useCallback(async () => {
    const tid = (tenantId || '').trim();
    if (!tid) {
      setMembers([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const token = getRestApiBearerToken();
      if (!token) throw new Error('Not signed in');
      const data = await listWorkspaceMembers(API_BASE_URL, token, tid);
      setMembers(Array.isArray(data.members) ? data.members : []);
    } catch (e) {
      setLoadError(e.message || 'Could not load members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedMembers = useMemo(() => {
    if (selectedEmails.length === 0) return [];
    const byEmail = new Map(members.map((m) => [norm(m.email), m]));
    return selectedEmails.map((em) => ({
      email: em,
      member: byEmail.get(em) || null,
    }));
  }, [members, selectedEmails]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = members.filter((m) => {
      if ((m.status || 'ACTIVE').toUpperCase() === 'REVOKED') return false;
      const em = norm(m.email);
      if (!em) return false;
      if (selectedSet.has(em)) return false;
      if (excludedSet.has(em)) return false;
      return true;
    });
    if (!q) {
      return [...list]
        .sort((a, b) =>
          (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''),
        )
        .slice(0, 40);
    }
    return list
      .filter((m) => {
        const name = (m.displayName || '').toLowerCase();
        const em = (m.email || '').toLowerCase();
        return name.includes(q) || em.includes(q);
      })
      .slice(0, 50);
  }, [members, query, selectedSet, excludedSet]);

  useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (m) => {
    if (disabled) return;
    const em = norm(m.email);
    if (!em || selectedSet.has(em)) return;
    onChange([...selectedEmails, em]);
    setQuery('');
  };

  const removeOne = (em) => {
    if (disabled) return;
    const target = norm(em);
    onChange(selectedEmails.filter((e) => e !== target));
  };

  const clearAll = () => {
    if (disabled) return;
    onChange([]);
    setQuery('');
  };

  const tid = (tenantId || '').trim();
  if (!tid) {
    return (
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        Choose an active workspace (tenant) before assigning supervisors.
      </p>
    );
  }

  return (
    <div ref={rootRef} className="space-y-2">
      {emptyHint ? (
        <p className="text-xs text-gray-500">{emptyHint}</p>
      ) : null}

      {loadError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {loadError}
        </p>
      )}

      {selectedMembers.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          {selectedMembers.map(({ email, member }) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-0.5 text-xs"
            >
              <span className="font-medium text-gray-800">
                {member?.displayName || email}
              </span>
              {member?.email && member.email !== (member?.displayName || '') ? (
                <span className="text-gray-500">· {member.email}</span>
              ) : null}
              <button
                type="button"
                onClick={() => removeOne(email)}
                disabled={disabled}
                className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                aria-label={`Remove ${email}`}
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled}
            className="ml-auto text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            Clear all
          </button>
        </div>
      ) : null}

      <div className="relative">
        <input
          type="text"
          autoComplete="off"
          placeholder={
            loading
              ? 'Loading members…'
              : placeholder || 'Search workspace members by name or email…'
          }
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={loading || !!loadError || disabled}
          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
        />
        {open && !loadError && (
          <ul
            className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            role="listbox"
          >
            {loading && (
              <li className="px-3 py-2 text-sm text-gray-500">Loading…</li>
            )}
            {!loading && members.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">
                No members found for this workspace.
              </li>
            )}
            {!loading && members.length > 0 && filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">
                No matching members. Try another search.
              </li>
            )}
            {!loading &&
              filtered.map((m) => (
                <li key={m.userSub || m.email}>
                  <button
                    type="button"
                    className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-blue-50"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(m)}
                  >
                    <span className="font-medium text-gray-900">
                      {m.displayName || m.email || 'Member'}
                    </span>
                    <span className="text-xs text-gray-500">{m.email}</span>
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">
                      {m.role}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}
